import {Component, ElementRef, ViewChild, ViewEncapsulation, inject, Input} from '@angular/core';
import {map, take} from 'rxjs';
import {INode, NgeExplorerConfig} from '../../shared/types';
import {displayFolderName} from '../../shared/display';
import {ExplorerService} from '../../services/explorer.service';
import {ViewSwitcherComponent} from '../view-switcher/view-switcher.component';
import {CONFIG} from '../../shared/providers';
import {AsyncPipe} from '@angular/common';
import {TablerIconsModule} from 'angular-tabler-icons';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonToggleModule} from '@angular/material/button-toggle';
import {MaterialModule} from '../../material.module';
import {TranslateModule, TranslateService} from '@ngx-translate/core';


@Component({
  selector: 'nxe-menu-bar',
  templateUrl: './menu-bar.component.html',
  styleUrls: ['./menu-bar.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [ViewSwitcherComponent, AsyncPipe, TablerIconsModule, MaterialModule, MatButtonModule, MatIconModule, MatButtonToggleModule, TranslateModule],
})
export class MenuBarComponent {
  @Input() showSwitcher: boolean = true;
  @Input() showSendFile: boolean = false;
  @Input() showEditFile: boolean = false;
  @Input() showViewFile: boolean = true;
  @ViewChild('uploader', {static: true}) uploader!: ElementRef;

  protected explorerService: ExplorerService = inject(ExplorerService);
  protected config: NgeExplorerConfig = inject(CONFIG);
  private translate: TranslateService = inject(TranslateService);

  protected featDelete = this.config.features?.delete;
  protected featUpload = this.config.features?.upload;
  protected featDownload = this.config.features?.download;
  protected featRename = this.config.features?.rename;
  protected featCreateDir = this.config.features?.createDir;
  protected featViewFile = this.config.features?.viewFile;
  protected featSendFile = this.config.features?.sendFile;

  /** selezione corrente: quando è vuota la barra blu notte delle azioni non compare */
  protected selection$ = this.explorerService.selection$;

  protected canOpenFolder$ = this.explorerService.selection$.pipe(map((n) => n.length === 1 && !n[0].isLeaf));
  protected canDownload$ = this.explorerService.selection$.pipe(map((n) => n.length === 1 && n[0].isLeaf));
  protected canDelete$ = this.explorerService.selection$.pipe(map((n) => n.length > 0));
  protected canRename$ = this.explorerService.selection$.pipe(map((n) => n.length === 1));
  protected canView$ = this.explorerService.selection$.pipe(map((n) => n.length === 1 && n[0].isLeaf));
  protected canSendFile$ = this.explorerService.selection$.pipe(map((n) => n.length === 1 && n[0].isLeaf));
  protected canEditFile$ = this.explorerService.selection$.pipe(map((n) => n.length === 1 && n[0].isLeaf));

  /** Nome dell'elemento selezionato oppure "N elementi selezionati". */
  selectionLabel(nodes: INode[]): string {
    if (nodes.length === 1) {
      const node = nodes[0];
      return node.isLeaf
        ? node.name
        : displayFolderName(node.name || '', this.translate.currentLang || this.translate.defaultLang);
    }
    return this.translate.instant('filemanager.ui.selected_count', {count: nodes.length});
  }

  clearSelection() {
    this.explorerService.select([]);
  }

  openFolder() {
    this.explorerService.selection$
      .pipe(take(1))
      .subscribe((nodes) => {
        if (nodes.length === 1 && !nodes[0].isLeaf) {
          this.explorerService.openNode(nodes[0].id);
        }
      });
  }

  createDir() {
    const name = prompt(this.translate.instant('filemanager.ui.prompt_new_folder'));
    if (name) {
      this.explorerService.createDir(name);
    }
  }

  refresh() {
    this.explorerService.refresh();
  }

  rename() {
    this.explorerService.selection$
      .pipe(
        take(1),
        map((n) => n[0])
      )
      .subscribe((node) => {
        const oldName = node.name;
        const newName = prompt(this.translate.instant('filemanager.ui.prompt_rename'), oldName);
        if (newName) {
          this.explorerService.rename(newName);
        }
      });
  }

  remove() {
    if (confirm(this.translate.instant('filemanager.ui.confirm_delete'))) {
      this.explorerService.remove();
    }
  }

  openUploader() {
    this.uploader.nativeElement.click();
  }

  viewFile() {
    this.explorerService.viewFile();
  }

  sendFile() {
    this.explorerService.sendFile();
  }

  editFile() {
    this.explorerService.editFile();
  }

  handleFiles(event: Event) {
    const files = (event.target as HTMLInputElement).files;
    if (!files || files.length === 0) {
      return;
    }
    this.explorerService.upload(files);
    this.uploader.nativeElement.value = '';
  }

  download() {
    this.explorerService.download();
  }
}
